import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Mail, Lock, ArrowLeft } from "lucide-react";

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, acceptDisclaimer } = useAuth();

  const disclaimerAccepted = location.state?.disclaimerAccepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        const userData = await login(email, password);
        toast.success("Welcome back!");
        // If user hasn't accepted disclaimer yet, navigate there
        if (!userData.disclaimer_accepted) {
          navigate("/disclaimer");
        } else {
          navigate("/persona");
        }
      } else {
        await register(email, password);
        // For new registrations from disclaimer flow, accept it automatically
        if (disclaimerAccepted) {
          await acceptDisclaimer();
        }
        toast.success("Account created!");
        // New users who came from disclaimer should go to persona
        // Others should go to disclaimer first
        navigate(disclaimerAccepted ? "/persona" : "/disclaimer");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex-1 flex flex-col"
      >
        {/* Back button */}
        <button
          onClick={() => navigate("/disclaimer")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        {/* Logo */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-3xl font-bold tracking-tight">
            Ventaphy<span className="text-primary">.</span>
          </h1>
        </div>

        {/* Form */}
        <div className="flex-1">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-card/50 backdrop-blur-sm rounded-3xl p-6 border border-border/50"
          >
            <h2 className="font-serif text-2xl font-bold mb-2 text-center">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              {isLogin 
                ? "Enter your details to continue" 
                : "Join the safe space"}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-muted-foreground">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="h-12 pl-12 rounded-xl bg-input/50 border-transparent focus:border-primary"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-muted-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 pl-12 rounded-xl bg-input/50 border-transparent focus:border-primary"
                    data-testid="password-input"
                  />
                </div>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-xs text-primary hover:underline mt-1"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-full text-lg font-semibold bg-primary hover:bg-primary/90 mt-6"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : isLogin ? (
                  "Sign In"
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          </motion.div>

          {/* Toggle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-6"
          >
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? (
                <>Don't have an account? <span className="text-primary font-semibold">Sign Up</span></>
              ) : (
                <>Already have an account? <span className="text-primary font-semibold">Sign In</span></>
              )}
            </button>
          </motion.div>
        </div>

        {/* Pricing link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center pt-8 pb-4"
        >
          <button
            onClick={() => navigate("/pricing")}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="view-pricing-btn"
          >
            View Pricing Plans
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
